import { useState } from 'react';
import { mockBorrowerAccess, mockConditions, mockLoans } from '../../services/mockData';

const BorrowerPortalPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loanNumber, setLoanNumber] = useState('');
  const [lastSSN, setLastSSN] = useState('');
  const [authError, setAuthError] = useState('');
  const [loan, setLoan] = useState(null);
  const [conditions, setConditions] = useState([]);
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [uploadComment, setUploadComment] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const handleAuthenticate = (e) => {
    e.preventDefault();
    setAuthError('');

    // Find matching borrower access
    const access = mockBorrowerAccess.find(
      a => a.loanNumber === loanNumber && a.borrowerLastSSN === lastSSN
    );

    if (access) {
      // Check if access has expired
      if (new Date(access.expiresAt) < new Date()) {
        setAuthError('This access link has expired. Please contact your loan officer for a new link.');
        return;
      }

      // Load loan and conditions
      const foundLoan = mockLoans.find(l => l.id === access.loanId);
      const borrowerConditions = mockConditions.filter(
        c => c.loanId === access.loanId && c.assignedTo.toLowerCase() === 'borrower'
      );

      setLoan(foundLoan);
      setConditions(borrowerConditions);
      setIsAuthenticated(true);
    } else {
      setAuthError('Invalid loan number or SSN. Please check your information and try again.');
    }
  };

  const handleUploadDocument = () => {
    if (!selectedCondition) return;

    alert(
      `Documents uploaded for condition: ${selectedCondition.title}\n\n` +
      `Files: ${uploadedFiles.length}\n` +
      `Comment: ${uploadComment}\n\n` +
      `Documents will be pushed directly into the LOS and your loan officer will be notified.`
    );

    // Reset form
    setSelectedCondition(null);
    setUploadComment('');
    setUploadedFiles([]);
  };

  if (!isAuthenticated) {
    return (
      <div className="page page-center">
        <div className="container container-tight py-4">
          <div className="text-center mb-4">
            <h1 className="display-6">Borrower Portal</h1>
            <p className="text-secondary">Upload documents for your loan application</p>
          </div>

          <div className="card">
            <div className="card-body">
              <h2 className="card-title mb-4">Secure Access</h2>
              
              <div className="alert alert-info">
                <div className="d-flex">
                  <div><i className="ti ti-info-circle me-2"></i></div>
                  <div>
                    <strong>Authentication Required</strong>
                    <div className="text-secondary small">
                      Enter your loan number and the last 4 digits of your Social Security Number to access your loan.
                      This information was provided by your loan officer.
                    </div>
                  </div>
                </div>
              </div>

              {authError && (
                <div className="alert alert-danger">
                  <i className="ti ti-alert-circle me-2"></i>
                  {authError}
                </div>
              )}

              <form onSubmit={handleAuthenticate}>
                <div className="mb-3">
                  <label className="form-label required">Loan Number</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g., UFF-2024-001"
                    value={loanNumber}
                    onChange={(e) => setLoanNumber(e.target.value)}
                    required
                  />
                  <small className="form-hint">
                    Your loan number was provided in the email from your loan officer
                  </small>
                </div>

                <div className="mb-3">
                  <label className="form-label required">Last 4 Digits of SSN</label>
                  <input
                    type="password"
                    className="form-control"
                    placeholder="••••"
                    maxLength="4"
                    value={lastSSN}
                    onChange={(e) => setLastSSN(e.target.value.replace(/\D/g, ''))}
                    required
                  />
                  <small className="form-hint">
                    Enter only the last 4 digits of your Social Security Number
                  </small>
                </div>

                <button type="submit" className="btn btn-primary w-100">
                  <i className="ti ti-login me-1"></i>
                  Access My Loan
                </button>
              </form>

              <div className="text-center mt-4">
                <small className="text-secondary">
                  <i className="ti ti-lock me-1"></i>
                  Your information is secure and encrypted
                </small>
              </div>
            </div>
          </div>

          <div className="text-center mt-3">
            <small className="text-secondary">
              Need help? Contact your loan officer
            </small>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-wrapper">
        <div className="page-header d-print-none">
          <div className="container-xl">
            <div className="row g-2 align-items-center">
              <div className="col">
                <h2 className="page-title">Welcome, {loan?.borrower?.firstName}!</h2>
                <div className="text-secondary">
                  Loan Number: <strong>{loan?.loanNumber}</strong>
                </div>
              </div>
              <div className="col-auto">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setIsAuthenticated(false);
                    setLoan(null);
                    setConditions([]);
                    setLoanNumber('');
                    setLastSSN('');
                  }}
                >
                  <i className="ti ti-logout me-1"></i>
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="page-body">
          <div className="container-xl">
            <div className="row row-deck row-cards">
              {/* Loan Status Card */}
              <div className="col-12">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">Loan Status</h3>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-3">
                        <div className="mb-2 text-secondary">Status</div>
                        <div className="h3">
                          <span className={`badge ${
                            loan?.status === 'In Progress' ? 'bg-blue' :
                            loan?.status === 'Approved' ? 'bg-green' :
                            loan?.status === 'Submitted' ? 'bg-cyan' : 'bg-gray'
                          }`}>
                            {loan?.status}
                          </span>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="mb-2 text-secondary">Loan Amount</div>
                        <div className="h3">${loan?.loanAmount?.toLocaleString()}</div>
                      </div>
                      <div className="col-md-3">
                        <div className="mb-2 text-secondary">Property Address</div>
                        <div className="fw-bold">{loan?.property?.address}</div>
                        <div className="text-secondary small">
                          {loan?.property?.city}, {loan?.property?.state} {loan?.property?.zipCode}
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="mb-2 text-secondary">Loan Officer</div>
                        <div className="fw-bold">Michael Chen</div>
                        <div className="text-secondary small">Prime Mortgage Group</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Conditions Card */}
              <div className="col-12">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">Your Action Items</h3>
                    <div className="text-secondary">
                      {conditions.filter(c => c.status === 'open' || c.status === 'rejected').length} items need your attention
                    </div>
                  </div>
                  <div className="card-body">
                    {conditions.length === 0 ? (
                      <div className="empty">
                        <div className="empty-icon">
                          <i className="ti ti-circle-check"></i>
                        </div>
                        <p className="empty-title">All caught up!</p>
                        <p className="empty-subtitle text-secondary">
                          You have no pending action items at this time.
                        </p>
                      </div>
                    ) : (
                      <div className="list-group list-group-flush">
                        {conditions.map(condition => (
                          <div key={condition.id} className="list-group-item">
                            <div className="row align-items-center">
                              <div className="col">
                                <div className="d-flex align-items-center">
                                  <span className={`badge me-2 ${
                                    condition.priority === 'High' ? 'bg-red' : 'bg-yellow'
                                  }`}>
                                    {condition.priority}
                                  </span>
                                  <div>
                                    <div className="fw-bold">{condition.title}</div>
                                    <div className="text-secondary small">{condition.description}</div>
                                    {condition.dueDate && (
                                      <div className="text-secondary small mt-1">
                                        <i className="ti ti-calendar me-1"></i>
                                        Due: {new Date(condition.dueDate).toLocaleDateString()}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="col-auto">
                                <span className={`badge ${
                                  condition.status === 'open' ? 'bg-yellow-lt' :
                                  condition.status === 'submitted' ? 'bg-blue-lt' :
                                  condition.status === 'satisfied' ? 'bg-green-lt' : 'bg-red-lt'
                                }`}>
                                  {condition.status}
                                </span>
                              </div>
                              <div className="col-auto">
                                {(condition.status === 'open' || condition.status === 'rejected') && (
                                  <button
                                    className="btn btn-primary"
                                    onClick={() => setSelectedCondition(condition)}
                                  >
                                    <i className="ti ti-upload me-1"></i>
                                    Upload Documents
                                  </button>
                                )}
                              </div>
                            </div>
                            
                            {condition.status === 'rejected' && condition.rejectionReason && (
                              <div className="alert alert-warning mt-2 mb-0">
                                <strong>Rejected:</strong> {condition.rejectionReason}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {selectedCondition && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Upload Documents</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setSelectedCondition(null);
                    setUploadComment('');
                    setUploadedFiles([]);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <h4 className="mb-3">{selectedCondition.title}</h4>
                <p className="text-secondary">{selectedCondition.description}</p>

                <div className="mb-3">
                  <label className="form-label">Select Files</label>
                  <input
                    type="file"
                    className="form-control"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files).map(f => ({
                        name: f.name,
                        size: f.size
                      }));
                      setUploadedFiles(files);
                    }}
                  />
                  <small className="form-hint">
                    You can select multiple files. Accepted formats: PDF, JPG, PNG
                  </small>
                </div>

                {uploadedFiles.length > 0 && (
                  <div className="mb-3">
                    <label className="form-label">Selected Files:</label>
                    <div className="list-group">
                      {uploadedFiles.map((file, idx) => (
                        <div key={idx} className="list-group-item">
                          <div className="d-flex align-items-center">
                            <i className="ti ti-file me-2"></i>
                            <div className="flex-fill">
                              <div>{file.name}</div>
                              <div className="text-secondary small">
                                {Math.round(file.size / 1024)} KB
                              </div>
                            </div>
                            <button
                              className="btn btn-sm btn-ghost-danger"
                              onClick={() => {
                                setUploadedFiles(uploadedFiles.filter((_, i) => i !== idx));
                              }}
                            >
                              <i className="ti ti-x"></i>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label">Comments (Optional)</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Add any notes or comments about these documents..."
                    value={uploadComment}
                    onChange={(e) => setUploadComment(e.target.value)}
                  ></textarea>
                </div>

                <div className="alert alert-info">
                  <div className="d-flex">
                    <div><i className="ti ti-info-circle me-2"></i></div>
                    <div>
                      <strong>Direct to LOS</strong>
                      <div className="text-secondary small">
                        Your documents will be uploaded directly into the loan origination system
                        and your loan officer will be notified immediately.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-link link-secondary"
                  onClick={() => {
                    setSelectedCondition(null);
                    setUploadComment('');
                    setUploadedFiles([]);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUploadDocument}
                  disabled={uploadedFiles.length === 0}
                >
                  <i className="ti ti-upload me-1"></i>
                  Upload {uploadedFiles.length} {uploadedFiles.length === 1 ? 'File' : 'Files'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BorrowerPortalPage;
